import React, { useState, useEffect, useRef, useMemo } from "react";
import { Howl } from "howler";
import startSound from "./start.wav"

import {
  Button,
  Row,
  Col,
  Tag,
  Input,
  Modal,
  Radio,
  Select,
  Tour,
} from "antd";
import "../App.css"; // Make sure to import your CSS file
import {
  AlertOutlined,
} from "@ant-design/icons";
import { CopyOutlined } from "@ant-design/icons";
import PowerGauge from "./PowerGauge";
import { max } from "lodash";
import HomeTrainerProgress from "./HomeTrainerProgress";
import TrainingLiveChart from "./TrainingLiveChart";
import YouTubeEmbed from "./YouTubeEmbed";
import VideoConf from "./VideoConf";
import SpotifyTab from "./SpotifyTab";
import { useDialog } from "./DialogProvider";
import { useInstall } from "./InstallProvider";
import SaveTraining from "./SaveTraining";
import HomeTrainerControls from "./HomeTrainerControls";
import FTPControls from "./FTPControls";
import HomeTrainerMobile from "./HomeTrainerMobile";
import HomeTrainerConnectControls from "./HomeTrainerConnectControls";
import HomeTrainerScreenControl from "./HomeTrainerScreenControl";
import IntervalProgressHud from "./IntervalProgressHud";
import NavigationWarningModal from "./NavigationWarningModal";
import { useHomeTrainerBluetooth } from "./HomeTrainerBluetooth";
import { resolveActiveStepMode, isErgActive } from "../utils/ergMode";
import {
  TRAINER_LINK,
  canRunSession,
  canDriveTrainer,
  hasTrainerData,
} from "../utils/trainerLink";
import {
  resolveGradientMode,
  createGradientState,
  getGradient,
  setGradient,
  adjustGradient,
} from "../utils/gradientMode";
import TotalSessionTimeHud from "./TotalSessionTimeHud";
import { onboardingConfig } from "../config/onboardingConfig";
const initialStates = {
  lastUpdatePower: Date.now(),
  lastUpdateCad: Date.now(),
  lastInsert: Date.now(),
  serviceFTMS: null,
  currentCadence: null,
  currentBPM: null,
  currentPower: null,
  serviceCP: null,
  serviceCSCS: null,
  serviceBPM: null,
  isConnected: false,
  isBPMConnected: false,
  totTime: 0,
  totTimeStep: 0,
  totTimePause: 0,
  totTimePauseStep: 0,
  currentHTValueCP : null,
  currentHTValueCSCS : null,
  startTimeSincePause: Date.now(),
  startTimeSinceStep: Date.now(),
  startTimeSinceStepPause: Date.now(),
  startTime: Date.now(),
  indexStep: 0,
  currentTargetPower: 50,
  pause: true,
  simulate: false,
  count: 0,
  currentERG: 50,
  additionalPower: 0,
  powerInfo: {
    data: [],
    avgTot: 0,
    avgSteps: [0],
    durationSteps: [0],
    slidding: 0,
    sliddingCad: 0,
    bestMinuteW: 0,
  },
  tcxLink: "",
  timerSec: -1,
  intervalId: null,
  intervalSimulationId: null,
  targetSecWatt: [],
  deviceHT: null,
  deviceBPM: null,
  modalPlanInfoIsOpen: false,
};

function HomeTrainer({
  onConnect,
  onDisconnect,
  initSteps,
  FTP,
  setStepIndex,
  isAdmin,
  setFTP,
  weight,
  setWeight,
  // Plan navigation helpers (used by mobile Plan Info modal)
  username,
  plansInfo,
  completedSessionsCount,
  completedSessionsCountLoaded,
  planCategoryForNav,
  previousUuid,
  selectPlan,
  getAllTrainingPlansInfo,
  getPlanLabelByUuid,
  infoToSave,
  otherUserInfo,
  setOtherUserInfo,
  isMobile,
  setDisabledPhoneButton,
  isMute,
  goToPlanSetup,
  onToggleMute,
  onRequestPremium,
  siderCollapsed = false,
  onSessionSavedUiConfirmed,
}) {
  const { alert: alertDlg, confirm } = useDialog();
  // PWA install (centralized)
  const { isStandalone, promptInstall } = useInstall();

  // Must exist before bluetooth hook (hook needs current simulate flag)
  const [simulate, setSimulate] = useState(initialStates.simulate);

  // Keep PowerGauge the same height as the whole "screen control" block on the right.
  // (Avoids height="100%" becoming unbounded when parent height isn't explicitly set.)
  const screenControlBlockRef = useRef(null);
  const [powerGaugeHeightPx, setPowerGaugeHeightPx] = useState(320);

  useEffect(() => {
    const el = screenControlBlockRef.current;
    if (!el) return;

    const applyHeight = (h) => {
      const next = Math.min(900, Math.max(240, Math.round(Number(h) || 0)));
      if (!next) return;
      setPowerGaugeHeightPx((prev) => (prev === next ? prev : next));
    };

    // Initial measure
    applyHeight(el.getBoundingClientRect().height);

    if (typeof ResizeObserver === "undefined") {
      const onResize = () => applyHeight(el.getBoundingClientRect().height);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    const ro = new ResizeObserver((entries) => {
      const entry = entries && entries[0];
      if (!entry) return;
      applyHeight(entry.contentRect?.height);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Use the Bluetooth hook
  const bluetooth = useHomeTrainerBluetooth(alertDlg, confirm, isMobile, simulate);

  // Extract values from Bluetooth hook
  const {
    serviceFTMS,
    serviceCP,
    serviceCSCS,
    isConnected,
    isBPMConnected,
    canErgControl,
    currentCadence,
    currentPower,
    currentBPM,
    lastUpdatePower,
    lastUpdateCad,
    currentHTValueCP,
    currentHTValueCSCS,
    setCurrentCadence,
    setCurrentPower,
    setCurrentBPM,
    setLastUpdatePower,
    setLastUpdateCad,
    setCurrentHTValueCP,
    setCurrentHTValueCSCS,
    connectToDevice: connectToDeviceBt,
    disconnectFromDevice: disconnectFromDeviceBt,
    setTargetPowerViaBt: setTargetPowerViaBtBt,
    connectToBPM: connectToBPMBt,
    disconnectFromBPM: disconnectFromBPMBt,
    ergEnabled,
    setErgEnabled,
    releaseErg,
    engageErg,
    canSimulateFlat,
    setSimulationGradient,
  } = bluetooth;

  // Keep old names used throughout the file, but backed by the shared hook
  // (Connect choice modal is handled by HomeTrainerConnectControls.)
  const connectToDevice = (opts) => connectToDeviceBt(onConnect, onDisconnect, opts);
  const disconnectFromDevice = () => disconnectFromDeviceBt(onDisconnect);
  const connectToBPM = () => connectToBPMBt();
  const disconnectFromBPM = () => disconnectFromBPMBt();
  const [soundHasPlayDuringStep, setSoundHasPlayDuringStep] = useState(false);
  const [browserWarningOpen, setBrowserWarningOpen] = useState(false);
  const [hasAcknowledgedBrowserWarning, setHasAcknowledgedBrowserWarning] = useState(false);

  const [modalPlanInfoIsOpen, setModalPlanInfoIsOpen] = useState(initialStates.modalPlanInfoIsOpen);
  const [totTime, setTotTime] = useState(initialStates.totTime);
  const [totTimeStep, setTotTimeStep] = useState(initialStates.totTimeStep);
  const [totTimePause, setTotTimePause] = useState(initialStates.totTimePause);
  const [totTimePauseStep, setTotTimePauseStep] = useState(
    initialStates.totTimePauseStep
  );
  const [startTimeSincePause, setStartTimeSincePause] = useState(
    initialStates.startTimeSincePause
  );
  const [startTimeSinceStep, setStartTimeSinceStep] = useState(
    initialStates.startTimeSinceStep
  );
  const [startTimeSinceStepPause, setStartTimeSinceStepPause] = useState(
    initialStates.startTimeSinceStepPause
  );
  const [startTime, setStartTime] = useState(initialStates.startTime); // Constant, no setter needed
  const [indexStep, setIndexStep] = useState(initialStates.indexStep);
  const [currentTargetPower, setCurrentTargetPower] = useState(
    initialStates.currentTargetPower
  );
  const [goNextWarning, setGoNextWarning] = useState(false);
  const [pause, setPause] = useState(initialStates.pause);
  const [currentERG, setCurrentERG] = useState(initialStates.currentERG);
  const [additionalPower, setAdditionalPower] = useState(
    initialStates.additionalPower
  );
  const [powerInfo, setPowerInfo] = useState(initialStates.powerInfo);
  const [timerSec, setTimerSec] = useState(initialStates.timerSec);
  const [intervalId, setIntervalId] = useState(initialStates.intervalId);
  const [intervalSimulationId, setIntervalSimulationId] = useState(
    initialStates.intervalSimulationId
  );
  const [targetSecWatt, setTargetSecWatt] = useState(
    initialStates.targetSecWatt
  );
  const [reset, setReset] = useState(0);
  const [steps, setSteps] = useState([...initSteps]);
  const currentStepMode = resolveActiveStepMode(steps, indexStep);

  // Three remembered simulated gradients, one per non-ERG mode. Not persisted:
  // they return to their defaults on every successful connection, the same rule
  // as ergEnabled.
  const [gradientState, setGradientState] = useState(createGradientState);

  // Riding with no Bluetooth link to the trainer at all. Set from the "Ride
  // without a trainer" button in the connect modal, cleared by Disconnect. This
  // is the only new piece of state: the link itself is derived, so there is no
  // second source of truth to keep in sync with isConnected.
  const [noTrainerRide, setNoTrainerRide] = useState(false);

  const trainerLink = useMemo(() => {
    if (noTrainerRide) return TRAINER_LINK.NO_TRAINER;
    if (!isConnected) return TRAINER_LINK.NONE;
    return canErgControl ? TRAINER_LINK.FTMS : TRAINER_LINK.SENSORS_ONLY;
  }, [noTrainerRide, isConnected, canErgControl]);

  // infoToSave is assembled by the parent and passed down as a prop; it knows
  // nothing about the trainer link, which only exists here. Carry it along on
  // the way to SaveTraining so the saved session records how it was ridden.
  //
  // This field exists to mark a ride that had no power to record, and it
  // describes the ride rather than the instant Save was pressed. A session
  // that ever saw real watts counts normally, whether the trainer dropped out
  // near the end or only connected after the rider had given up on it. When
  // there is nothing to mark, the key is left off entirely, which is exactly
  // the shape of every session saved before this field existed.
  const sawRealPower = (powerInfo.data || []).some((d) => d.watt > 0);
  const infoToSaveWithLink =
    !sawRealPower && trainerLink === TRAINER_LINK.NO_TRAINER
      ? { ...infoToSave, trainer_link: TRAINER_LINK.NO_TRAINER }
      : infoToSave;

  const sessionRunnable = canRunSession({ link: trainerLink, simulate });

  // A simulated gradient is something we send to a trainer. With no trainer on
  // the line there is nothing to send it to, so there is no gradient to speak
  // of, and the display falls through to the badge instead.
  const gradientMode = hasTrainerData({ link: trainerLink, simulate })
    ? resolveGradientMode({
        canErgControl: canErgControl || simulate,
        ergEnabled,
        stepMode: currentStepMode,
      })
    : null;
  const gradient = getGradient(gradientState, gradientMode);

  // The toggle is only meaningful on a trainer the app can actually drive.
  const showErgToggle = canDriveTrainer({ link: trainerLink, simulate });

  // Disconnect is the single exit from every link, including the one that never
  // connected anything. canRunSession turns false as soon as this clears, so all
  // the session locks close on their own.
  //
  // The no-trainer ride asks its own question instead of calling
  // disconnectFromDevice: that function's "Disconnect home trainer ?" is both
  // the wrong question (no trainer was ever connected) and asked too late for
  // this flag (it clears noTrainerRide first, so answering No would still end
  // the ride). There is no device to tear down and no FTMS state to reset in
  // this mode, so clearing the flag plus the onDisconnect callback -- the two
  // things disconnectFromDevice still does that apply here -- are all that is
  // needed; canRunSession then closes the session locks the same way a real
  // disconnect does.
  const handleDisconnect = async (...args) => {
    if (noTrainerRide) {
      if (!(await confirm("End the ride without a trainer ?"))) return;
      setNoTrainerRide(false);
      onDisconnect();
      return;
    }
    return disconnectFromDevice(...args);
  };

  // Entering the fallback mode also drops whatever the sensors last
  // reported. A trainer that dropped without firing a disconnect event, or
  // a teardown that failed partway through (nothing in this app listens for
  // gattserverdisconnected), can leave currentPower/currentCadence frozen at
  // a real reading with isConnected already false. Without this, that
  // frozen number would be recorded on every sample of the no-trainer ride.
  // This is a second line of defence: the recording effect also guards on
  // noTrainerRide directly, but clearing here keeps the stale value from
  // being visible even before the first sample is recorded.
  const handleRideWithoutTrainer = () => {
    setNoTrainerRide(true);
    setCurrentPower(null);
    setCurrentCadence(null);
  };

  // A connection that lands after the rider gave up takes over. The modal that
  // offered the fallback can be on screen while a real connect attempt is still
  // in flight, so this is reachable, not theoretical. Clearing the flag here
  // rather than reordering the memo keeps one resolved source of truth: a later
  // disconnect then lands on NONE, which ends the session, instead of silently
  // dropping back into a ride nobody asked for a second time.
  useEffect(() => {
    if (isConnected) setNoTrainerRide(false);
  }, [isConnected]);

  // Reset the three gradients on every successful connection. Watching
  // isConnected rather than hooking the connect callback keeps this independent
  // of which connect path was taken -- onConnect is a prop owned by the parent.
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (isConnected && !wasConnectedRef.current) {
      setGradientState(createGradientState());
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected]);

  const setTargetPowerViaBt = async (
    service,
    watts,
    stepMode = currentStepMode
  ) => {
    await setTargetPowerViaBtBt(service, watts, stepMode);
    // Preserve previous behavior: also store the last ERG value applied
    setCurrentERG(watts);
  };
  const [totTimeFinal, setTotTimeFinal] = useState(initSteps.map((s) => s.duration).reduce((accumulator, currentValue) => accumulator + currentValue, 0))
  const [remainingTimeStep, setRemainingTimeStep] = useState(indexStep < steps.length && indexStep >= 0
    ? steps[indexStep].duration - totTimeStep
    : 0);
  const [navigationWarningOpen, setNavigationWarningOpen] = useState(false);
  const [pendingNavigationKey, setPendingNavigationKey] = useState(null);
  const [isSessionSaved, setIsSessionSaved] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [nextTargetPower, setNextTargetPower] = useState(initSteps.length > 1 ? initSteps[1].watt : null)
  // view toggles
  const [viewMode, setViewMode] = useState("live"); // 'live' | 'youtube' | 'meet' | 'spotify'
  const [tourOpen, setTourOpen] = useState(false);
  const [tourCurrent, setTourCurrent] = useState(0);
  const [tourNudgeOpen, setTourNudgeOpen] = useState(false);
  const tourNudgeTimerRef = useRef(null);

  const dismissTourNudge = () => {
    try {
      localStorage.setItem(onboardingConfig.tourHasBeenShownStorageKey, "true");
    } catch {}
    setTourNudgeOpen(false);
    try {
      tourNudgeTimerRef.current && clearTimeout(tourNudgeTimerRef.current);
    } catch {}
    tourNudgeTimerRef.current = null;
  };

  // First-time nudge (non-admin only): highlight the Tour button for 20s.
  useEffect(() => {
    if (isMobile) return;
    if (isAdmin) return;
    if (!completedSessionsCountLoaded) return;
    if (typeof completedSessionsCount !== "number") return;
    if (completedSessionsCount > onboardingConfig.progressCountTourThreshold) return;
    let alreadyShown = false;
    try {
      alreadyShown = localStorage.getItem(onboardingConfig.tourHasBeenShownStorageKey) === "true";
    } catch {}
    if (alreadyShown) return;

    setTourNudgeOpen(true);
    try {
      tourNudgeTimerRef.current && clearTimeout(tourNudgeTimerRef.current);
    } catch {}
    tourNudgeTimerRef.current = setTimeout(() => {
      dismissTourNudge();
    }, 20000);

    return () => {
      try {
        tourNudgeTimerRef.current && clearTimeout(tourNudgeTimerRef.current);
      } catch {}
      tourNudgeTimerRef.current = null;
    };
  }, [isAdmin, isMobile, completedSessionsCountLoaded, completedSessionsCount]);

  const tourSteps = [
    {
      title: "Connect trainer (ERG)",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Connect your trainer via Bluetooth.</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
          <li>
              Some trainers are unfortunately not compatible with ERG controls - if that happens with yours,
              you can still use the app in sensors-only mode and control ERG via a third-party app. 
              We'd love your feedback (brand + model).
            </li>
            <li>iPhone/iPad web apps don’t support Bluetooth trainers.</li>
            <li>Keep this tab visible while training (background tabs can miss data).</li>
            
          </ul>
        </div>
      ),
      target: () => document.getElementById("tour-connect-trainer"),
    },
    {
      title: "Connect heart-rate sensor",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Optional, but recommended.</div>
          <div>Connect your HR strap/sensor to record BPM during the session.</div>
        </div>
      ),
      target: () => document.getElementById("tour-connect-hr"),
    },
    {
      title: "Mute (beep / no beep)",
      description: (
        <div>
          <div>
            If the button is <strong>red</strong>: no “beep beep”.
          </div>
          <div style={{ marginTop: 6 }}>
            If it’s not red: you’ll hear a “beep beep” near the end of each interval.
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-mute"),
    },
    {
      title: "Play (start)",
      description: (
        <div>
          <div>Once connected, press Play to start the workout.</div>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            If you paused, Play will resume the session.
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-play"),
    },
    {
      title: "Pause",
      description: (
        <div>
          <div>Use Pause during the session to pause your workout.</div>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            Tip: you must be paused to reset, disconnect devices, or save.
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-pause"),
    },
    {
      title: "Reset (only when paused)",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Resets your session.</div>
          <div>
            This asks for confirmation, then resets everything. <strong>It can’t be undone.</strong>
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-reset"),
    },
    {
      title: "Next interval (Skip)",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Skip the current interval step.</div>
          <div>
            No confirmation. <strong>It can’t be undone.</strong> Use with care.
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-skip"),
    },
    {
      title: "Save + Strava upload",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Save your session and upload to Strava.</div>
          <div>
            When you click Save, you can save the workout and (optionally) upload it to Strava.
          </div>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            You can connect Strava at that moment, or before from your Profile section (bottom of the sidebar).
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-save"),
    },
    {
      title: "FTP down",
      description: (
        <div>
          <div>Decrease FTP for the rest of the session.</div>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            All targets stay as a % of FTP - lowering FTP makes the workout easier.
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-ftp-down"),
    },
    {
      title: "FTP up",
      description: (
        <div>
          <div>Increase FTP for the rest of the session.</div>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            All targets stay as a % of FTP - increasing FTP makes the workout harder.
          </div>
        </div>
      ),
      target: () => document.getElementById("tour-ftp-up"),
    },
    {
      title: "Total time HUD",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Your full session timer.</div>
          <div>Shows elapsed time and total planned time for the workout.</div>
        </div>
      ),
      target: () => document.getElementById("tour-total-hud"),
    },
    {
      title: "Interval HUD",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Current interval timer.</div>
          <div>Shows how much time you’ve spent in the current step and what’s remaining.</div>
        </div>
      ),
      target: () => document.getElementById("tour-interval-hud"),
    },
    {
      title: "Workout screen controls",
      description: (
        <div>
          <div>Live status and targets for your current step (power/cadence/BPM, target watt, next step, etc.).</div>
        </div>
      ),
      target: () => document.getElementById("tour-screen-controls"),
    },
    {
      title: "Plan progress",
      description: (
        <div>
          <div>Shows your full workout structure and where you are in the plan.</div>
        </div>
      ),
      target: () => document.getElementById("tour-plan-progress"),
    },
    {
      title: "Views (Live chart / YouTube / Live session)",
      description: (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Choose what you want to see.</div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li><strong>Live chart</strong>: your data in real time.</li>
            <li><strong>YouTube</strong>: play a video while training.</li>
            <li><strong>Live session</strong>: join a live training call.</li>
          </ul>
        </div>
      ),
      target: () => document.getElementById("tour-views"),
    },
    {
      title: "Report a bug",
      description: (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Found a bug?</div>
          <div>Use the floating button (bottom right) to report it. Screenshots + details help a lot.</div>
        </div>
      ),
      target: () => document.getElementById("tour-report-bug"),
    },
  ];

  useEffect(() => {
    // Allow deep-linking to Spotify tab after OAuth callback: /app?tab=spotify
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const tab = (sp.get("tab") || "").toLowerCase();
      if (tab === "spotify") {
        // Spotify disabled for now (Spotify dev app creation is temporarily blocked).
        // Keep the tab visible but disabled in the UI.
        setViewMode("live");
      }
    } catch {}
  }, []);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeId, setYoutubeId] = useState(null);
  const [motivationalVideos, setMotivationalVideos] = useState([]);
  const [meetingUrl, setMeetingUrl] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [leaveSignal, setLeaveSignal] = useState(0);
  // chart data managed by TrainingLiveChart

  const prevSteps = useRef()

  const incrementFtpTimeoutRef = useRef(null); // Référencer le timeout actif
  const wakeLocklistenerAdded = useRef(false); // Déclaration au niveau supérieur
  // chart refs moved to TrainingLiveChart component


  const handleAdjustAdditionalFtp = (increment) => {
    // Ajouter à la "buffer zone"

    // Effacer le timeout existant
    if (incrementFtpTimeoutRef.current) {
      clearTimeout(incrementFtpTimeoutRef.current);
    }

    if (additionalPower + increment + FTP > 10) {
      setAdditionalPower((prev) => (prev + increment))
    }
    
    // Programmer l'application de la mise à jour après 500 ms
    incrementFtpTimeoutRef.current = setTimeout(() => {
      
      const newTempFTP = FTP + additionalPower + increment //+ increment because additionalPower is not updated yet here
      //setPower((prevPower) => pirevPower + incrementFtpClickBuffer.current); // Appliquer l'incrément
      if (newTempFTP > 10) {
        console.log(`Apply increment of ${additionalPower + increment}. New FTP : ${newTempFTP} W`)

        setSteps(steps.map((x, i) => ({ ...x, watt: Math.round((initSteps[i].watt / FTP) * (newTempFTP)) })))
        
      }
    }, 1000); // Délai d'attente avant d'appliquer
  };

  // Stable sound instance (avoid recreating Howl on every render)
  const f1StartSoundRef = useRef(null);
  useEffect(() => {
    try {
      f1StartSoundRef.current = new Howl({
        src: [startSound],
        volume: 1.0,
        rate: 0.8,
        preload: true,
      });
    } catch (e) {
      f1StartSoundRef.current = null;
    }
    return () => {
      try {
        // Stop/unload to avoid leaking audio resources
        f1StartSoundRef.current && f1StartSoundRef.current.unload();
      } catch (e) {
        // ignore
      } finally {
        f1StartSoundRef.current = null;
      }
    };
  }, []);

  // If the user mutes while a sound is playing, stop it immediately.
  useEffect(() => {
    if (!isMute) return;
    try {
      f1StartSoundRef.current && f1StartSoundRef.current.stop();
    } catch (e) {
      // ignore
    }
  }, [isMute]);

  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      if ("wakeLock" in navigator) {
        try {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLock.addEventListener("release", () => {
            console.log("Wake Lock was released");
          });
          console.log("Wake Lock is active");
        } catch (err) {
          console.error("Failed to acquire wake lock:", err);
        }
      } else {
        console.error("Wake Lock API not supported");
      }
    };

    const releaseWakeLock = async () => {
      if (wakeLock !== null) {
        try {
          await wakeLock.release();
          console.log("Wake Lock was released");
        } catch (err) {
          console.error("Failed to release wake lock:", err);
        }
        wakeLock = null;
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && sessionRunnable) {
        await requestWakeLock();
      } else {
        await releaseWakeLock();
      }
    };

    // Ajouter le listener uniquement si ce n'est pas déjà fait
    if (!wakeLocklistenerAdded.current) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      wakeLocklistenerAdded.current = true; // Marque que le listener a été ajouté
    }

    // Gestion des Wake Locks en fonction des dépendances
    if (sessionRunnable) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      // Retirer le listener et libérer le Wake Lock lors du nettoyage
      if (wakeLocklistenerAdded.current) {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        wakeLocklistenerAdded.current = false; // Marque que le listener a été retiré
      }
      releaseWakeLock();
    };
  }, [sessionRunnable]);

  useEffect(() => {

    prevSteps.current = [...initSteps];
    console.log("Reset state");
    const wasSimulating = simulate;
    // keeping connection with bt alive
    setSteps(initSteps.map(x => x))
    setLastUpdatePower(Date.now());
    setLastUpdateCad(Date.now());
    setCurrentCadence(initialStates.currentCadence);
    setCurrentPower(initialStates.currentPower);
    setCurrentBPM(initialStates.currentBPM);
    setTotTime(initialStates.totTime);
    setTotTimeStep(initialStates.totTimeStep);
    setTotTimePause(initialStates.totTimePause);
    setTotTimePauseStep(initialStates.totTimePauseStep);
    setStartTimeSincePause(Date.now());
    setStartTimeSinceStep(Date.now());
    setStartTimeSinceStepPause(Date.now());
    setStartTime(Date.now());
    setIndexStep(initialStates.indexStep);
    setPause(initialStates.pause);
    // Keep current simulation mode on reset; reset should not implicitly disable simulation
    setCurrentERG(initialStates.currentERG);
    setAdditionalPower(initialStates.additionalPower);
    setPowerInfo(initialStates.powerInfo);
    setTimerSec(initialStates.timerSec);
    // Clear running intervals first, then reset ids.
    if (intervalId !== null) {
      clearInterval(intervalId);
      console.log("Clearing interval");
    }
    intervalSimulationId && clearInterval(intervalSimulationId);
    setIntervalId(initialStates.intervalId);
    setIntervalSimulationId(initialStates.intervalSimulationId);
    setStepIndex(initialStates.indexStep);
    setCurrentHTValueCP(initialStates.currentHTValueCP);
    setCurrentHTValueCSCS(initialStates.currentHTValueCSCS);
    setSoundHasPlayDuringStep(false)
    setTotTimeFinal(initSteps.map((s) => s.duration).reduce((accumulator, currentValue) => accumulator + currentValue, 0));
    setNextTargetPower(initSteps.length > 1 ? initSteps[1].watt : null);
    setRemainingTimeStep(indexStep < initSteps.length && indexStep >= 0
      ? initSteps[indexStep].duration - totTimeStep
      : 0);

    const watt = [];
    initSteps.forEach((s) => {
      for (let i = 0; i < s.duration; i++) {
        for (let j = 0; j < 1; j++) {
          watt.push(s.watt);
        }
      }
    });
    //setCurrentTargetPower(steps.length > 0 ? steps[0].watt : null)
    setTargetSecWatt(watt);
    setCurrentTargetPower(initSteps.length > 0 ? initSteps[0].watt : 50);
    // This will log the updated value

    // This code runs after the `simulate` state updates

    setDisabledPhoneButton(false)

    // chart data handled by TrainingLiveChart
    // If we were simulating, restarting the sim loop keeps watts/cadence flowing after reset.
    if (wasSimulating) {
      simulateTrainer();
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, initSteps, FTP]); //steps

  useEffect(() => {
    if (totTime === 0 && isMobile && steps.length > 0) {
      setModalPlanInfoIsOpen(true)
    }

    const watt = [];
    steps.forEach((s) => {
      for (let i = 0; i < s.duration; i++) {
        for (let j = 0; j < 1; j++) {
          watt.push(s.watt);
        }
      }
    });
    //setCurrentTargetPower(steps.length > 0 ? steps[0].watt : null)
    setTargetSecWatt(watt);
    setCurrentTargetPower(steps.length > 0 ? steps[0].watt : 50);
    setTotTimeFinal(steps.map((s) => s.duration).reduce((accumulator, currentValue) => accumulator + currentValue, 0));

    setRemainingTimeStep(indexStep < steps.length && indexStep >= 0
      ? steps[indexStep].duration - totTimeStep
      : 0);
    setNextTargetPower(steps.length > indexStep + 1 ? steps[indexStep + 1].watt : null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  useEffect(() => {
    const timer = () => {
      if (timerSec === -1) {
        console.log("Init timer");
        const id = setInterval(() => {
          setTimerSec((prevData) => {
            return prevData + 1;
          });
        }, 100);
        setIntervalId(id); // Store interval ID
      }
    };

    timer();
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        console.log("Clearing interval");
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initSteps]);

  useEffect(() => {
    // Use functional updates to avoid stale `powerInfo` (and to keep deps manageable).
    setPowerInfo((prev) => {
      const safePrev = prev || initialStates.powerInfo;
      if (!pause) {
        const newData = [
          ...(safePrev.data || []),
          {
            step: indexStep,
            // currentPower stays null for the whole ride in "no trainer"
            // mode (and briefly on a real trainer, before the first power
            // notification arrives). Recording null here is what makes the
            // TCX builder raise on save, so coalesce to 0. Use ?? and not
            // ||: a genuine 0 reading from a real trainer must stay 0.
            //
            // The noTrainerRide check on top of that ?? 0 is not redundant:
            // a trainer that drops without firing a disconnect event (no
            // listener in this app catches gattserverdisconnected), or a
            // teardown that throws before its setCurrentPower(null) runs,
            // leaves currentPower frozen at its last real reading, not
            // null. Once the rider falls back to this mode that frozen
            // number is no longer real, so it is forced to 0/null here
            // regardless of what the stale state still holds.
            watt: noTrainerRide ? 0 : currentPower ?? 0,
            cad: noTrainerRide ? null : currentCadence,
            bpm: currentBPM,
            ts: new Date().toISOString(),
            htValueCP: totTime <= 180 ? currentHTValueCP : null,
            htValueCSCS: totTime <= 180 ? currentHTValueCSCS : null,
          },
        ]; // tracking raw HT value for debug but as heavy, only take first 3 min

        // Calculate total average
        const newAvgTot =
          newData.map((x) => x.watt).reduce((acc, curr) => acc + curr, 0) /
          newData.length;

        const dataStep = newData
          .filter((x) => x.step === indexStep)
          .map((x) => x.watt);

        const avgLastStep =
          dataStep.reduce((acc, curr) => acc + curr, 0) / dataStep.length;

        const newAvgStep = [
          ...(safePrev.avgSteps || []).slice(0, indexStep),
          avgLastStep,
        ];

        const newDurationSteps = [
          ...(safePrev.durationSteps || []).slice(0, indexStep),
          totTimeStep,
        ];

        // Prepare for sliding window average
        const slidingData = newData.slice(-4); // last 4 elements
        const newSliding =
          slidingData.map((x) => x.watt).reduce((acc, curr) => acc + curr, 0) /
          slidingData.length;
        const newSlidingCadence =
          slidingData.map((x) => x.cad).reduce((acc, curr) => acc + curr, 0) /
          slidingData.length;

        const lastMin = newData
          .filter(
            (x) => x.ts >= new Date(new Date().getTime() - 60000).toISOString()
          )
          .map((x) => x.watt);
        const avgLastMin =
          lastMin.reduce((acc, curr) => acc + curr, 0) / lastMin.length;
        const newBestMinuteW =
          safePrev.bestMinuteW && safePrev.bestMinuteW >= avgLastMin
            ? safePrev.bestMinuteW
            : avgLastMin;

        return {
          data: newData,
          avgTot: newAvgTot,
          avgSteps: newAvgStep,
          durationSteps: newDurationSteps,
          slidding: newSliding,
          sliddingCad: newSlidingCadence,
          bestMinuteW: newBestMinuteW,
        };
      }

      // Pause: keep last averages but still reflect live instant values.
      // Same frozen-reading guard as the recording branch above: a stale
      // currentPower must not show as a live reading once the ride has
      // fallen back to no-trainer mode.
      return {
        ...safePrev,
        slidding: noTrainerRide ? 0 : currentPower,
        sliddingCad: noTrainerRide ? null : currentCadence,
      };
    });

    setRemainingTimeStep(indexStep < steps.length && indexStep >= 0
      ? steps[indexStep].duration - totTimeStep
      : 0);

    if (!soundHasPlayDuringStep && steps.length > 0 && indexStep < steps.length && steps[indexStep].duration - totTimeStep < 4 && steps[indexStep].duration - totTimeStep > 2) {
      setSoundHasPlayDuringStep(true);
      if (!isMute) {
        try {
          f1StartSoundRef.current && f1StartSoundRef.current.play();
        } catch (e) {
          // ignore
        }
      }
    }

  }, [
    pause,
    currentPower,
    currentCadence,
    currentBPM,
    noTrainerRide,
    lastUpdatePower,
    lastUpdateCad,
    currentHTValueCP,
    currentHTValueCSCS,
    totTime,
    // Fix: include `isMute` (otherwise mute toggle is ignored due to stale closure)
    isMute,
    // Also include values used by the sound trigger condition
    soundHasPlayDuringStep,
    steps,
    indexStep,
    totTimeStep,
  ]);


  // (moved) cadence/avg/watt main screen UI lives in `HomeTrainerScreenControl`

  const simulateTrainer = () => {
    // Check to ensure we don't start multiple intervals
    const withBpm = !isBPMConnected;

    intervalSimulationId && clearInterval(intervalSimulationId);
    console.log("Simulation started");
    const id = setInterval(() => {
      const randomPower = Math.max(
        Math.floor(Math.random() * 40) + currentERG - 20,
        0
      );
      const randomBPM =
        Math.floor(Math.random() * 20) +
        Math.min(Math.max(randomPower / FTP + 30, 80), 160) -
        10; // Random power between 100 and 200
      const randomCadence = Math.floor(Math.random() * 3) + 90; // Random cadence between 90 and 100
      setCurrentCadence(randomCadence);
      setCurrentPower(randomPower);
      withBpm && setCurrentBPM(randomBPM);
    }, 250);
    setIntervalSimulationId(id);
  };

  useEffect(() => {
    if (simulate) {
      simulateTrainer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulate, currentERG]);

  // chart scroll/visibility handled in TrainingLiveChart

  // Clean up interval when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      intervalId && clearInterval(intervalId);
      intervalSimulationId && clearInterval(intervalSimulationId);
      isConnected && disconnectFromDevice();
      isBPMConnected && disconnectFromBPM();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Calculer les watts ciblés en fonction du temps écoulé
  const calculateTargetWatt = (elapsedTime) => {
    let accumulatedDuration = 0;
    let i = 0;
    for (const step of steps) {
      accumulatedDuration += step.duration;
      if (elapsedTime < accumulatedDuration) {
        return { targetWatt: step.watt, index: i };
      }
      i += 1;
    }
    // Past the last step: no prescribed effort. The 50 is a display fallback
    // only — resolveActiveStepMode makes this a free ride, and the release path
    // ignores wattage entirely.
    return { targetWatt: 50, index: i };
  };

  useEffect(() => {
    if (!pause) {
      const currentTime = Date.now();
      const elapsedTime = Math.floor(
        (currentTime - startTime - totTimePause) / 1000
      ); // Temps en secondes
      const elapsedTimeStep = Math.floor(
        (currentTime - startTimeSinceStep - totTimePauseStep) / 1000
      ); // Temps en secondes
      setTotTime(elapsedTime);
      setTotTimeStep(elapsedTimeStep);
      const stepInfo = calculateTargetWatt(elapsedTime);
      if (stepInfo.targetWatt && (indexStep !== stepInfo.index)) {
        setNextTargetPower(indexStep < steps.length - 2 ? steps[indexStep + 2].watt : null);
        setIndexStep(indexStep + 1);
        setStepIndex(indexStep + 1); //use in other state
        setSoundHasPlayDuringStep(false)
        setTotTimePauseStep(0);
        setStartTimeSinceStep(Date.now());
        // `indexStep` still points at the outgoing step here, so read the mode
        // from the step we are entering.
        setTargetPowerViaBt(
          serviceFTMS,
          stepInfo.targetWatt,
          resolveActiveStepMode(steps, stepInfo.index)
        );
        setCurrentTargetPower(stepInfo.targetWatt);
      }
      else if (stepInfo.targetWatt !== currentTargetPower) {
        setTargetPowerViaBt(serviceFTMS, stepInfo.targetWatt);
        setCurrentTargetPower(stepInfo.targetWatt);
      }
      // else if (targetSecWatt.length > 0 && elapsedTime > targetSecWatt.length && currentTargetPower != 0) {//finish
      //     setTargetPowerViaBt(serviceFTMS, 50)
      //     setCurrentTargetPower(0)
      //     setIndexStep(indexStep + 1)
      //     setStepIndex(indexStep + 1) //use in other state
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerSec, powerInfo, startTime, steps, targetSecWatt, lastUpdatePower, lastUpdateCad]);

  // live chart updates handled in TrainingLiveChart
  const extractYouTubeId = (url) => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      let id = null;
      if (host === "youtu.be") {
        id = u.pathname.split("/")[1];
      } else if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
        if (u.pathname === "/watch") {
          id = u.searchParams.get("v");
        } else if (u.pathname.startsWith("/embed/")) {
          id = u.pathname.split("/")[2];
        } else if (u.pathname.startsWith("/shorts/")) {
          id = u.pathname.split("/")[2];
        }
      }
      return id && /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
    } catch {
      return null;
    }
  };

  const handleLoadYouTube = () => {
    const id = extractYouTubeId(youtubeUrl);
    if (!id) {
      alertDlg("Please enter a valid YouTube link.");
      setYoutubeId(null);
      return;
    }
    setYoutubeId(id);
    setViewMode("youtube");
  };

  useEffect(() => {
    // Load predefined videos from backend to avoid CORS
    const loadVideos = async () => {
      try {
        let data = null;
        const res = await fetch("/api/youtube_videos/", { cache: "no-store" });
        if (res.ok) data = await res.json();
        else console.error("Failed to load videos", res.statusText);
        if (!data) return;
        if (Array.isArray(data)) {
          setMotivationalVideos(
            data
              .filter((x) => x && x.name && x.url)
              .map((x) => ({ name: x.name, url: x.url }))
          );
        }
      } catch (_) {
        // ignore if not reachable
      }
    };
    loadVideos();
  }, []);


  const goNextStep = () => {
    console.log("Next step")
    //skipping message as it blocks the flow if not pause
    // not showing at all
    setSoundHasPlayDuringStep(true)
    if (true || !pause || goNextWarning) {
      setSteps(steps.map((x, i) => i !== indexStep ? { ...x } : { ...x, duration: totTimeStep + 1 }))
    }
    setGoNextWarning(true)



  }

  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      if ('wakeLock' in navigator) {
        try {
          wakeLock = await navigator.wakeLock.request('screen');
          wakeLock.addEventListener('release', () => {
            console.log('Wake Lock was released');
          });
          console.log('Wake Lock is active');
        } catch (err) {
          console.error('Failed to acquire wake lock:', err);
        }
      } else {
        console.error('Wake Lock API not supported');
      }
    };

    if (sessionRunnable && isMobile) {
      requestWakeLock();
    }

    // Clean up the wake lock on component unmount or if sessionRunnable changes
    return () => {
      if (wakeLock !== null && isMobile) {
        wakeLock.release().then(() => {
          console.log('Wake Lock was released by cleanup');
        }).catch(err => {
          console.error('Failed to release wake lock:', err);
        });
        wakeLock = null;
      }
    };
  }, [sessionRunnable, isMobile]);

  // PWA install state is handled by InstallProvider

  const handleInstallApp = async () => {
    try {
      const result = await promptInstall();
      if (!result || result.status === "unavailable" || result.status === "error") {
        // Fallback guidance for Safari/macOS or when prompt not available
        alertDlg(
          "Install not available or the app is already installed (it won't work on private browsing mode).\n\nSafari (macOS): Safari → Add to Dock.\nChrome: Address bar ‘Install’ icon or 3‑dots → Save and install.\nEdge: 3‑dots → Apps → Install this site as an app."
        );
      }
    } catch {
      // ignore
    } finally {
      // Close the warning and do NOT start training
      setBrowserWarningOpen(false);
    }
  };

  const resumeTraining = () => {
    setDisabledPhoneButton(true);
    console.log("Resume");
    setTotTimePause(totTimePause + Date.now() - startTimeSincePause);
    setTotTimePauseStep(
      totTimePauseStep + Date.now() - startTimeSinceStepPause
    );
    setTargetPowerViaBt(serviceFTMS, currentTargetPower);
    setPause(false);
  };

  const onPause = (p) => {
    if (p) {
      console.log("Pause");
      setPause(true);
      setStartTimeSincePause(Date.now());
      setStartTimeSinceStepPause(Date.now());
      // Only meaningful while we actually drive the trainer. In manual or
      // free-ride mode the rider owns the resistance, so we say nothing.
      // `canErgControl || simulate` mirrors the predicate the hook itself uses
      // (`canErgControlRef.current || simulateRef.current`): in simulation the
      // raw flag is always false, and the bare form silently skipped this write.
      if (isErgActive({ canErgControl: canErgControl || simulate, ergEnabled, stepMode: currentStepMode })) {
        setTargetPowerViaBt(serviceFTMS, 50);
      }
    } else {
      // Show browser warning only on desktop when the app is not installed
      if (totTime === 0 && !hasAcknowledgedBrowserWarning && !isMobile && !isStandalone) {
        setBrowserWarningOpen(true);
        return;
      }
      resumeTraining();
      //subscribeToStatus(serviceFTMS);
    }
  };

  // Only reachable while paused (the control itself is disabled otherwise).
  const onToggleErg = async (next) => {
    setErgEnabled(next);
    if (next) {
      // Request Control only — deliberately no target write here. We are paused,
      // so nothing could go out anyway, but the attempt would still record
      // `desiredTargetW`; resumeTraining() then re-sends the same wattage and
      // anti-jitter swallows it, leaving the trainer on flat road for the rest
      // of the step. The release left `desiredTargetW` and `lastSentTargetW`
      // null, so resumeTraining()'s own call passes anti-jitter and sends the
      // 0x05. If this engage failed, that same call re-acquires control through
      // the released branch of setTargetPowerViaBt.
      const engaged = await engageErg();
      if (!engaged) {
        // Deliberately not reverting ergEnabled: leaving it true is what arms
        // the automatic re-engage in setTargetPowerViaBt's released branch on
        // resume. This failure is fail-safe (flat road, not unexpected load),
        // but the release path warns on both its failure branches and this one
        // was silent, so log for parity.
        console.warn("ERG engage failed; will retry on resume");
      }
    } else {
      const released = await releaseErg(
        "manual-toggle",
        getGradient(gradientState, "manual")
      );
      if (!released) {
        // The trainer is still holding its ERG target. Put the toggle back so
        // the line does not claim a manual control the rider does not have.
        setErgEnabled(true);
        alertDlg(
          "Could not hand control back to the trainer.\n\nERG control is still active and the trainer is still holding its target. Try turning ERG control off again, or disconnect the trainer."
        );
      }
    }
  };

  // Adjust the gradient for whichever mode currently applies. Reachable only
  // while ERG is off and the trainer can simulate -- the control itself
  // enforces both -- so no extra guard is needed here.
  const onAdjustGradient = (delta) => {
    if (!gradientMode) return;
    const next = adjustGradient(gradient, delta);
    if (next === gradient) return;
    setGradientState((prev) => setGradient(prev, gradientMode, next));
    setSimulationGradient(next);
  };

  // When the applicable gradient mode changes, push that mode's remembered
  // value. The release itself goes out flat from the hook, which does not know
  // the rider's per-mode settings; this follows with the right one. There is
  // therefore a brief window -- at most one rate-limit window -- where a step
  // runs at 0% before its own gradient lands.
  const lastGradientModeRef = useRef(null);
  useEffect(() => {
    if (lastGradientModeRef.current === gradientMode) return;
    lastGradientModeRef.current = gradientMode;
    if (!gradientMode) return;
    setSimulationGradient(getGradient(gradientState, gradientMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradientMode]);
  // Trainer Bluetooth/ERG logic extracted to `HomeTrainerBluetooth.js`


  function formatTime(seconds) {
    let reverse = false;
    if (seconds < 0) {
      seconds = -seconds;
      reverse = true;
    }
    const minutes = Math.floor(seconds / 60); // Get the full minutes
    const remainingSeconds = seconds % 60; // Get the remaining seconds
    // Return the formatted time string
    // Add a leading zero to seconds if less than 10
    const timeStr = reverse
      ? `- ${minutes}'${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`
      : `${minutes}'${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
    return timeStr;
  }

  // mm:ss helper moved to TrainingLiveChart

  // chart-specific helpers moved to TrainingLiveChart

  // Segment coloring for the Target dataset handled inline in dataset config


  // (removed) old total timer colors were only used by the previous inline progress row
  const controlHeight = 64;
  const controlHeightMobile = 52;

  // Plan badge (category - plan - tss)
  const selectedPlan = infoToSave?.selectedPlan || {};
  const planCategory =
    selectedPlan?.category ||
    selectedPlan?.plan_category ||
    selectedPlan?.training_category ||
    "";
  const planName =
    selectedPlan?.name ||
    selectedPlan?.plan_name ||
    selectedPlan?.title ||
    "";
  const planTssRaw =
    selectedPlan?.stress_points ??
    selectedPlan?.tss ??
    selectedPlan?.TSS ??
    null;
  const planTss =
    typeof planTssRaw === "number"
      ? Math.round(planTssRaw)
      : planTssRaw != null && planTssRaw !== ""
        ? Math.round(Number(planTssRaw))
        : null;
  const planBadgeText = (() => {
    const parts = [];
    if (planCategory) parts.push(planCategory);
    if (planName) parts.push(planName);
    if (planTss != null && !Number.isNaN(planTss)) parts.push(`${planTss} TSS`);
    return parts.join(" - ");
  })();

  const handleBackToSession = () => {
    setNavigationWarningOpen(false);
    setPendingNavigationKey(null);
  };

  const handleLeaveSession = () => {
    setNavigationWarningOpen(false);
    // Execute the pending navigation
    if (pendingNavigationKey) {
      if (pendingNavigationKey === 'plan-setup') {
        // Special case for plan setup navigation from HomeTrainerProgress
        goToPlanSetup();
      } else if (window.performNavigation) {
        window.performNavigation(pendingNavigationKey);
      }
    }
    setPendingNavigationKey(null);
  };

  const handleGoToPlanSetup = (e) => {
    // Keep this button reliable even inside nested glass panels
    try {
      e?.preventDefault?.();
      e?.stopPropagation?.();
    } catch {}

    const currentIsSessionActive = sessionRunnable && totTime > 0;
    if (currentIsSessionActive) {
      setPendingNavigationKey("plan-setup");
      setNavigationWarningOpen(true);
      return;
    }

    goToPlanSetup();
  };

  // Listen for navigation attempts from sidebar
  useEffect(() => {
    const handleNavigationAttempt = (event) => {
      const { navigationKey, callback } = event.detail;
      // Always check current session state
      const currentIsSessionActive = sessionRunnable && totTime > 0;
      if (currentIsSessionActive) {
        setPendingNavigationKey(navigationKey);
        setNavigationWarningOpen(true);
        callback(false); // Prevent navigation
      } else {
        callback(true); // Allow navigation
      }
    };

    window.addEventListener('navigationAttempt', handleNavigationAttempt);

    return () => {
      window.removeEventListener('navigationAttempt', handleNavigationAttempt);
    };
  }, [sessionRunnable, totTime]); // Include dependencies to ensure latest state



  const browserWarningModal = (
    <Modal
      title="Warning"
      open={browserWarningOpen}
      rootClassName="liquid-glass-modal"
      onCancel={() => setBrowserWarningOpen(false)}
      footer={[
        !isStandalone && (
          <Button
            key="install"
            onClick={handleInstallApp}
            disabled={isStandalone}
          >
            Install app
          </Button>
        ),
        <Button
          key="ok"
          type="primary"
          className="primary-glass-btn"
          onClick={() => {
            setHasAcknowledgedBrowserWarning(true);
            setBrowserWarningOpen(false);
            resumeTraining();
          }}
        >
          Start training
        </Button>,
      ].filter(Boolean)}
    >
      <div>
        <p style={{ fontWeight: 700, color: "rgba(200,0,0,0.95)" }}>
          <AlertOutlined /> Important browser limitation
        </p>
        <p>
          While training, <strong>keep this page visible</strong>. If you{" "}
          <strong>switch tabs</strong> or <strong>open other windows</strong>, the app
          may pause briefly and miss updates, creating{" "}
          <strong>gaps in your data</strong> and possibly{" "}
          <strong>reducing the distance saved to Strava</strong>.
        </p>
        {!isStandalone && (
          <p>
            For the best, uninterrupted experience,{" "}
            <strong>install the desktop app</strong>. Use the{" "}
            <em>Install app</em> button below.
          </p>
        )}
      </div>
    </Modal>
  );

  const handlePauseAndSave = () => {
    // Pause if not already paused
    if (!pause) {
      onPause(true);
    }
    // IMPORTANT: "Pause and Save" should NOT continue a pending navigation/logout.
    // Treat this as "Back to Session" + open the save modal.
    setPendingNavigationKey(null);
    setSaveModalOpen(true);
    setNavigationWarningOpen(false);
  };

  function calculateFTPDuringTest() {
    if (
      infoToSave.selectedPlan.username === "Test" &&
      infoToSave.selectedPlan.name === "20 min" &&
      !infoToSave.isModificationMade
    ) {
      if (powerInfo.avgSteps.length >= 8) {
        return Math.round(powerInfo.avgSteps[7] * 0.95);
      } else {
        return 0;
      }
    } else if (
      infoToSave.selectedPlan.username === "Test" &&
      infoToSave.selectedPlan.name === "1 hour" &&
      !infoToSave.isModificationMade
    ) {
      if (powerInfo.avgSteps.length >= 8) {
        return Math.round(powerInfo.avgSteps[7]);
      } else {
        return 0;
      }
    } else if (
      infoToSave.selectedPlan.username === "Test" &&
      infoToSave.selectedPlan.name === "Ramp Up" &&
      !infoToSave.isModificationMade
    ) {
      if (powerInfo.avgSteps.length >= 8) {
        return Math.round(powerInfo.bestMinuteW * 0.75);
      } else {
        return 0;
      }
    } else {
      return null;
    }
  }

  const connectionIssueDiv = (
    <div>
      <ul>
        <li>
          Ensure your trainer supports Bluetooth Low Energy (BLE) (not ANT+ / “classic” Bluetooth), <br/>
          and that Bluetooth is enabled on your device
        </li>
        <li>
          Unfortunately, iOS devices (iPhone, iPad) don't support Bluetooth on web apps.
          <br/> You can still use your computer, including MacBooks, for a seamless experience.
        </li>
        <li>
          Ensure you are using a compatible browser (such as Chrome, not Safari)
        </li>
        <li>
          Ensure your trainer is not connected to another device (phone,
          computer, ...)
        </li>
        <li>Try restarting your trainer</li>
        <li>
          Try restarting your computer (strangely, this often resolves the
          issue)
        </li>
        <li>
          If you can connect your trainer but no data is received, try
          disconnecting and reconnecting
        </li>
        <li>
          Send feedback with your trainer's brand and model, along with your
          device and browser type
        </li>
      </ul>
    </div>
  );
  if (!isMobile) {
    return (
      <div>
        <Tour
          open={tourOpen}
          current={tourCurrent}
          onChange={(c) => setTourCurrent(c)}
          onClose={() => setTourOpen(false)}
          steps={tourSteps}
        />
        {browserWarningModal}
        <NavigationWarningModal
          open={navigationWarningOpen}
          isSessionSaved={isSessionSaved}
          pause={pause}
          onBack={handleBackToSession}
          onLeave={handleLeaveSession}
          onPauseAndSave={handlePauseAndSave}
        />
        <div>
            <Row>
            <Col span={15}>
            <div className="glass-panel glass-section">
            <Row gutter={12} align="middle" wrap={false} style={{ width: "100%", alignItems: "center" }}>
                <Col flex="1 1 0" style={{ minWidth: 0 }}>
                  <HomeTrainerControls
                    isMobile={false}
                    isConnected={isConnected}
                    simulate={simulate}
                    sessionRunnable={sessionRunnable}
                    pause={pause}
                    isAdmin={isAdmin}
                    isSessionSaved={isSessionSaved}
                    totTime={totTime}
                    indexStep={indexStep}
                    steps={steps}
                    totTimeStep={totTimeStep}
                    onPause={onPause}
                    onReset={() => setReset(reset + 1)}
                    onSkip={goNextStep}
                    onSimulate={() => {
                      setSimulate(true);
                      simulateTrainer();
                    }}
                    onShowTour={() => {
                      dismissTourNudge();
                      setTourCurrent(0);
                      setTourOpen(true);
                    }}
                    tourNudgeOpen={tourNudgeOpen}
                    onDismissTourNudge={dismissTourNudge}
                    goNextStep={goNextStep}
                    confirm={confirm}
                    setIsSessionSaved={setIsSessionSaved}
                    setSaveModalOpen={setSaveModalOpen}
                    controlHeight={controlHeight}
                  />
                </Col>

                <Col flex={`${controlHeight * 5}px`} style={{ maxWidth: 520 }}>
                  <FTPControls
                    gradientMode={gradientMode}
                    gradient={gradient}
                    canSimulateFlat={canSimulateFlat || simulate}
                    onAdjustGradient={onAdjustGradient}
                    ergEnabled={ergEnabled}
                    onToggleErg={onToggleErg}
                    pause={pause}
                    showErgToggle={showErgToggle}
                    noTrainer={trainerLink === TRAINER_LINK.NO_TRAINER}
                    isMobile={false}
                    FTP={FTP}
                    additionalPower={additionalPower}
                    onAdjustFTP={handleAdjustAdditionalFtp}
                    controlHeight={controlHeight}
                  />
                </Col>
            </Row>
            </div>
            <p />
            <div id="tour-total-hud" className="glass-panel glass-section" style={{ padding: 0 }}>
              <TotalSessionTimeHud
                isMobile={false}
                elapsedSec={totTime}
                totalSec={totTimeFinal}
                formatTime={formatTime}
              />
            </div>

            <div className="glass-section">
            <Row gutter={12} style={{ alignItems: "stretch" }}>
              <Col span={10}>
                <div className="glass-section" style={{ background: "none" }}>
                  <PowerGauge
                    maxPower={max(steps.map((s) => s.watt))}
                    remainingTime={
                      indexStep < steps.length && indexStep >= 0
                        ? steps[indexStep].duration - totTimeStep
                        : 0
                    }
                    nextTargetPower={nextTargetPower}
                    targetPower={currentTargetPower ? currentTargetPower : 0}
                    currentPower={Math.round(powerInfo.slidding)}
                    isLast={indexStep === steps.length - 1}
                    paused={pause}
                    noTrainer={trainerLink === TRAINER_LINK.NO_TRAINER}
                    height={powerGaugeHeightPx}
                  />
                </div>
              </Col>
              <Col span={14}>
                <Row>
                  <Col span={24}>
                    <div ref={screenControlBlockRef}>
                      <p />
                      <Row>
                        <Col span={24}>
                          <div id="tour-interval-hud">
                            <IntervalProgressHud
                              isMobile={false}
                              remainingTimeSec={remainingTimeStep}
                              elapsedTimeSec={totTimeStep}
                              formatTime={formatTime}
                              style={{ marginBottom: 10 }}
                            />
                          </div>
                        </Col>
                      </Row>
                      <div id="tour-screen-controls">
                        <HomeTrainerScreenControl
                          isMobile={false}
                          steps={steps}
                          indexStep={indexStep}
                          totTimeStep={totTimeStep}
                          remainingTimeStep={remainingTimeStep}
                          currentTargetPower={currentTargetPower}
                          nextTargetPower={nextTargetPower}
                          powerInfo={powerInfo}
                          pause={pause}
                          FTP={FTP}
                          formatTime={formatTime}
                          bpm={currentBPM}
                          headerSuffix={(serviceCP || serviceCSCS) && !serviceFTMS ? "(Manual)" : ""}
                          tagClassName="custom-tag"
                          noTrainer={trainerLink === TRAINER_LINK.NO_TRAINER}
                        />
                      </div>
                      <p />
                      {/* <Tag color="blue" className="custom-tag">Instant W {currentPower}</Tag>
                                      <p /> */}
                      {calculateFTPDuringTest() != null && (
                        <>
                          {" "}
                          <Row>
                            <Col span={24}>
                              <Tag color="red" className="custom-tag">
                                Estimated FTP : {calculateFTPDuringTest()} W
                              </Tag>
                            </Col>
                          </Row>
                          <p />
                        </>
                      )}
                    </div>

                    {/* Moved Avg/RPM/BPM tags into HomeTrainerScreenControl */}
                  </Col>
                </Row>
              </Col>
              
            </Row>
            </div>
                      
            {/* PlaneGame removed */}
            </Col>
              <Col span={1}></Col>

                      <Col span={8}>
                        <HomeTrainerConnectControls
                          isMobile={false}
                          isBPMConnected={isBPMConnected}
                          simulate={simulate}
                          sessionRunnable={sessionRunnable}
                          canErgControl={canErgControl}
                          pause={pause}
                          isMute={isMute}
                          onConnect={connectToDevice}
                          onDisconnect={handleDisconnect}
                          onRideWithoutTrainer={handleRideWithoutTrainer}
                          connectToBPM={connectToBPM}
                          disconnectFromBPM={disconnectFromBPM}
                          onToggleMute={onToggleMute}
                          showTroublePopover={true}
                          connectionIssueDiv={connectionIssueDiv}
                          controlHeight={controlHeight}
                          onToggleErg={onToggleErg}
                        />
                        {!!planBadgeText && (
                          <div
                            className="glass-panel glass-section"
                            style={{
                              marginTop: 8,
                              padding: "10px 12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "rgba(0,0,0,0.55)",
                                  marginBottom: 2,
                                }}
                              >
                                Current plan
                              </div>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: "rgba(0,0,65,1)",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={planBadgeText}
                              >
                                {planBadgeText}
                              </div>
                            </div>
                          </div>
                        )}
                        <div id="tour-plan-progress" className="glass-panel glass-section" style={{ marginTop: 8 }}>
                         
                            <HomeTrainerProgress
                              steps={steps}
                              FTP={FTP}
                              currentIndex={indexStep}
                            />

                            {(!steps || steps.length === 0 || indexStep >= steps.length - 1) && (
                              <Button
                                type="primary"
                                className="primary-glass-btn"
                                block
                                style={{ marginTop: 10 }}
                                onClick={handleGoToPlanSetup}
                              >
                                Load plan or Create new
                              </Button>
                            )}
                          
                        </div>
                      </Col>
                      </Row>
            <Row gutter={0} style={{ marginTop: -32 }}>
              <Col span={24}>
                <Row gutter={8} style={{ marginTop: 1, marginBottom: 12 }}>
                  <Col span={12}>
                    <div id="tour-views">
                      <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
                        <Radio.Button value="live">Live chart</Radio.Button>
                        <Radio.Button value="youtube">YouTube</Radio.Button>
                        <Radio.Button value="meet">Live session</Radio.Button>
                        {(Boolean(siderCollapsed) || true) && (
                          <Radio.Button
                            value="spotify"
                            disabled
                            style={{
                              background: "rgba(255,255,255,0.78)",
                              border: "1px solid rgba(0,0,0,0.22)",
                              color: "rgba(0,0,0,0.55)",
                              fontWeight: 700,
                              opacity: 1, // keep readable even when disabled
                            }}
                          >
                            Spotify (soon)
                          </Radio.Button>
                        )}
                      </Radio.Group>
                    </div>
                  </Col>
                  {viewMode === "youtube" && (
                    <Col span={12} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>

                      <Col span={11}>
                        <Input
                          placeholder="YouTube link (e.g., https://youtube.com/...)"
                          value={youtubeUrl}
                          onChange={(e) => setYoutubeUrl(e.target.value)}
                          onPressEnter={handleLoadYouTube}
                        />
                      </Col>
                      <Col span={1} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color: "rgba(0,0,0,0.65)", fontWeight: 600 }}>OR</span>
                      </Col>
                      <Col span={13}>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Use motivational video"
                          value={null}
                          onChange={(value) => {
                            const vid = motivationalVideos.find((v) => v.url === value);
                            if (vid) {
                              const id = extractYouTubeId(vid.url);
                              if (id) {
                                setYoutubeUrl(vid.url);
                                setYoutubeId(id);
                              } else {
                                alertDlg("Invalid video URL in list.");
                              }
                            }
                          }}
                          options={motivationalVideos.map((v) => ({ label: v.name, value: v.url }))}
                        />
                      </Col>
                      <Col span={3}>
                        <Button block onClick={handleLoadYouTube}>
                          Load
                        </Button>
                      </Col>
                      </Col>
                  )}
                  {viewMode === "meet" && (meetingId || meetingUrl) && (
                    <Col span={12} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                      <span style={{ color: "rgba(0,0,0,0.65)", fontWeight: 600 }}>
                        {meetingId ? "Session ID" : "Session link"}
                      </span>
                      <span
                        style={{
                          maxWidth: 200,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.65)",
                          border: "1px solid rgba(0,0,0,0.12)",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                          color: "#0f172a",
                          fontWeight: 600,
                        }}
                        title={meetingId ? meetingId : meetingUrl}
                      >
                        {meetingId ? meetingId : meetingUrl}
                      </span>
                      <Button
                        size="small"
                        className="primary-glass-btn"
                        icon={<CopyOutlined />}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(meetingId ? meetingId : meetingUrl);
                          } catch {}
                        }}
                      />
                      <Button
                        size="small"
                        danger
                        onClick={() => {
                          setLeaveSignal((x) => x + 1);
                        }}
                      >
                        Leave
                      </Button>
                    </Col>
                  )}
                </Row>
                <div id="tour-media-panel" className="glass-panel glass-section" style={{ height: 420, width: "100%", marginTop: 4, marginBottom: 4, position: "relative", overflow: "hidden", padding: 0 }}>
                  {/* Keep YouTube mounted so playback persists when switching views */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      opacity: viewMode === "youtube" ? 1 : 0,
                      pointerEvents: viewMode === "youtube" ? "auto" : "none",
                      transition: "opacity 150ms ease",
                    }}
                  >
                    {youtubeId ? (
                      <YouTubeEmbed videoId={youtubeId} />
                    ) : (
                      <div
                        style={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "rgba(0,0,0,0.45)",
                        }}
                      >
                        Paste a valid YouTube link and click Load
                      </div>
                    )}
                  </div>
                  {/* Live chart layer */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      opacity: viewMode === "live" ? 1 : 0,
                      pointerEvents: viewMode === "live" ? "auto" : "none",
                      transition: "opacity 150ms ease",
                      background: viewMode === "live" ? "transparent" : "none",
                    }}
                  >
                    <TrainingLiveChart
                      ftp={FTP}
                      targetSecWatt={targetSecWatt}
                      power={powerInfo.slidding}
                      cadence={powerInfo.sliddingCad}
                      bpm={currentBPM}
                      totTime={totTime}
                      resetSignal={reset}
                      height={420}
                    />
                  </div>
                  {/* Spotify layer - keep mounted so the Web Playback SDK stays connected while switching views */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      opacity: viewMode === "spotify" ? 1 : 0,
                      pointerEvents: viewMode === "spotify" ? "auto" : "none",
                      transition: "opacity 150ms ease",
                      background: "transparent",
                      overflow: "auto",
                    }}
                  >
                    <SpotifyTab />
                  </div>
                  {/* Live session (Daily) layer - keep alive during an ongoing meeting even when switching tabs */}
                  {(viewMode === "meet" || meetingUrl) && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "transparent",
                        opacity: viewMode === "meet" ? 1 : 0,
                        pointerEvents: viewMode === "meet" ? "auto" : "none",
                        transition: "opacity 150ms ease",
                      }}
                    >
                      <VideoConf
                        onClose={() => { setViewMode("live"); setMeetingId(null); setMeetingUrl(null); }}
                        onSessionUrlChange={(url) => {
                          setMeetingUrl(url);
                          try {
                            const u = new URL(url);
                            const id = u.pathname.split("/").filter(Boolean).pop();
                            setMeetingId(id || null);
                          } catch {
                            setMeetingId(null);
                          }
                        }}
                        leaveSignal={leaveSignal}
                        onRequestPremium={onRequestPremium}
                      />
                    </div>
                  )}
                </div>
              </Col>
            </Row>
        </div>
        {/* Save Training Modal - Desktop */}
        <SaveTraining
          disabled={!pause || !sessionRunnable}
          weight={weight}
          steps={steps}
          initSteps={initSteps}
          data={powerInfo.data}
          FTP={FTP}
          setFTP={setFTP}
          setWeight={setWeight}
          infoToSave={infoToSaveWithLink}
          FTPResult={calculateFTPDuringTest()}
          otherUserInfo={otherUserInfo}
          setOtherUserInfo={setOtherUserInfo}
          externalOpen={saveModalOpen}
          onSaved={() => {
            setIsSessionSaved(true);
          }}
          onSavedUiConfirmed={() => {
            try {
              onSessionSavedUiConfirmed && onSessionSavedUiConfirmed();
            } catch { }
          }}
          onModalClose={() => {
            setSaveModalOpen(false);
            if (pendingNavigationKey) {
              if (pendingNavigationKey === 'plan-setup') {
                goToPlanSetup();
              } else if (window.performNavigation) {
                window.performNavigation(pendingNavigationKey);
              }
              setPendingNavigationKey(null);
            }
          }}
        />
      </div>
    );
  }
  else {
    return (
      <div>
        {browserWarningModal}
        <NavigationWarningModal
          open={navigationWarningOpen}
          isSessionSaved={isSessionSaved}
          pause={pause}
          onBack={handleBackToSession}
          onLeave={handleLeaveSession}
          onPauseAndSave={handlePauseAndSave}
        />
        <HomeTrainerMobile
          gradientMode={gradientMode}
          gradient={gradient}
          canSimulateFlat={canSimulateFlat || simulate}
          onAdjustGradient={onAdjustGradient}
          isConnected={isConnected}
          isBPMConnected={isBPMConnected}
          simulate={simulate}
          sessionRunnable={sessionRunnable}
          canErgControl={canErgControl}
          pause={pause}
          isMute={isMute}
          onConnect={connectToDevice}
          onDisconnect={handleDisconnect}
          onRideWithoutTrainer={handleRideWithoutTrainer}
          connectToBPM={connectToBPM}
          disconnectFromBPM={disconnectFromBPM}
          onToggleMute={onToggleMute}
          onRequestPremium={onRequestPremium}
          onSessionSavedUiConfirmed={onSessionSavedUiConfirmed}
          ergEnabled={ergEnabled}
          onToggleErg={onToggleErg}
          showErgToggle={showErgToggle}
          noTrainer={trainerLink === TRAINER_LINK.NO_TRAINER}

              steps={steps}
              initSteps={initSteps}
              FTP={FTP}
          additionalPower={additionalPower}
          indexStep={indexStep}
                      totTime={totTime}
          totTimeStep={totTimeStep}
          remainingTimeStep={remainingTimeStep}
          totTimeFinal={totTimeFinal}
          currentTargetPower={currentTargetPower}
          nextTargetPower={nextTargetPower}
          powerInfo={powerInfo}

          isAdmin={isAdmin}
          isSessionSaved={isSessionSaved}
          setIsSessionSaved={setIsSessionSaved}
          setSaveModalOpen={setSaveModalOpen}
          onPause={onPause}
          onReset={() => setReset(reset + 1)}
          onSkip={goNextStep}
          onSimulate={() => { setSimulate(true); simulateTrainer(); }}
          goNextStep={goNextStep}
          confirm={confirm}
          handleAdjustAdditionalFtp={handleAdjustAdditionalFtp}

          infoToSave={infoToSaveWithLink}
          username={username}
          plansInfo={plansInfo}
          planCategoryForNav={planCategoryForNav}
          previousUuid={previousUuid}
          selectPlan={selectPlan}
          getAllTrainingPlansInfo={getAllTrainingPlansInfo}
          getPlanLabelByUuid={getPlanLabelByUuid}
              weight={weight}
              setWeight={setWeight}
          setFTP={setFTP}
              FTPResult={calculateFTPDuringTest()}
              otherUserInfo={otherUserInfo}
              setOtherUserInfo={setOtherUserInfo}
          goToPlanSetup={goToPlanSetup}
          setNavigationWarningOpen={setNavigationWarningOpen}
          setPendingNavigationKey={setPendingNavigationKey}

          formatTime={formatTime}
          simulateTrainer={simulateTrainer}

          planInfoModalIsOpen={modalPlanInfoIsOpen}
          setPlanInfoModalIsOpen={setModalPlanInfoIsOpen}
          saveModalOpen={saveModalOpen}

          controlHeightMobile={controlHeightMobile}
        />
      </div>
    );
  }
  
}

export default HomeTrainer;
